// ============================================================================
// The Files viewers (docs/areas/files.md): as many types as the browser can
// show, all on the device. The bytes come straight from the bucket through a
// presigned URL (`getUrl`), so the owner's page and a share page draw the same
// way. Nothing a person uploaded is ever put in the page as markup: pictures
// and SVG are <img>, media are <video>/<audio>, PDFs are pdf.js on a canvas,
// and everything read as bytes (text, code, Markdown, JSON, CSV, zip, fonts)
// is parsed and drawn as text by React, capped in size.
// ============================================================================

import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ChevronRight, Download, FileQuestion, FolderArchive } from 'lucide-react'
import { CodeBlock, useCodeStyle } from '@/components/Code'
import { MdLite } from '@/lib/mdlite'
import { Skeleton } from '@/components/Skeleton'
import { Button, Failed, cx } from '@/components/ui'
import { parseDelimited, separatorFor } from '@/lib/files/csv'
import {
  CODE_LANG,
  CODE_MAX_LINES,
  CSV_MAX_ROWS,
  capFor,
  extOf,
  formatSize,
  readsBytes,
  typeLabel,
  viewerFor,
  type ViewerKind,
} from '@/lib/files/types'
import { listZip, rangeReader } from '@/lib/files/zip'
import { useAsync } from './useAsync'

const PdfViewer = lazy(() => import('./PdfViewer'))

export interface ViewFile {
  id: string
  name: string
  size?: number
  contentType?: string
}

export type GetUrl = (variant: 'view' | 'download') => Promise<string | undefined>

/** The bytes a reading viewer needs (up to its cap), fetched with a Range request. */
const readBytes = async (url: string, cap: number): Promise<{ bytes: ArrayBuffer; cut: boolean }> => {
  const res = await fetch(url, Number.isFinite(cap) ? { headers: { Range: `bytes=0-${cap - 1}` } } : {})
  if (!res.ok) throw new Error(`the bucket said ${res.status}`)
  const bytes = await res.arrayBuffer()
  const total = Number(res.headers.get('Content-Range')?.split('/')[1] ?? bytes.byteLength)
  return {
    bytes: bytes.byteLength > cap ? bytes.slice(0, cap) : bytes,
    cut: total > cap || bytes.byteLength > cap,
  }
}

const Frame = ({
  children,
  className,
  ...rest
}: {
  children: ReactNode
  className?: string
  'data-testid'?: string
}) => (
  <div className={cx('card overflow-hidden', className)} {...rest}>
    {children}
  </div>
)

/** Drawn when nothing better can be: what the file is, and Download. */
export const Details = ({
  file,
  onDownload,
  why,
}: {
  file: ViewFile
  onDownload?: () => void
  why?: string
}) => (
  <Frame className="px-4 py-8 flex flex-col items-center text-center gap-2" data-testid="file-details">
    <FileQuestion size={34} strokeWidth={1.5} className="text-muted" aria-hidden />
    <p className="text-[15px] font-medium break-all">{file.name}</p>
    <p className="text-sm text-muted">
      {typeLabel(file.name, file.contentType)}
      {file.size !== undefined && <span className="ml-3">{formatSize(file.size)}</span>}
    </p>
    <p className="text-xs text-faint max-w-xs">{why ?? 'This kind of file is not shown here.'}</p>
    {onDownload && (
      <Button variant="primary" className="mt-2" onClick={onDownload}>
        <Download size={16} aria-hidden /> Download
      </Button>
    )}
  </Frame>
)

const CutNote = ({ cut, what }: { cut: boolean; what: string }) =>
  cut ? <p className="text-xs text-faint mt-2">Showing {what}; download it for the rest.</p> : null

/** A JSON value as a tree: objects and arrays fold, the first two levels open. */
const JsonNode = ({ k, v, depth }: { k?: string; v: unknown; depth: number }) => {
  const [open, setOpen] = useState(depth < 2)
  const label = k !== undefined ? <span className="tk-prop">{JSON.stringify(k)}: </span> : null
  if (v === null || typeof v !== 'object') {
    const text = typeof v === 'string' ? JSON.stringify(v) : String(v)
    return (
      <div className="pl-4 break-all">
        {label}
        <span className={typeof v === 'string' ? 'tk-str' : typeof v === 'number' ? 'tk-num' : 'tk-cnst'}>
          {text}
        </span>
      </div>
    )
  }
  const entries = Array.isArray(v) ? v.map((x, i) => [String(i), x] as const) : Object.entries(v)
  const shown = entries.slice(0, 500)
  const [o, c] = Array.isArray(v) ? ['[', ']'] : ['{', '}']
  return (
    <div className="pl-4">
      <button type="button" className="text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
        <ChevronRight
          size={12}
          className={cx('inline -ml-4 mr-1 transition-transform', open && 'rotate-90')}
          aria-hidden
        />
        {label}
        {o}
        {!open && (
          <span className="text-faint">
            {' '}
            {entries.length} {Array.isArray(v) ? 'items' : 'keys'}{' '}
          </span>
        )}
        {!open && c}
      </button>
      {open && (
        <>
          {shown.map(([kk, vv]) => (
            <JsonNode key={kk} k={Array.isArray(v) ? undefined : kk} v={vv} depth={depth + 1} />
          ))}
          {entries.length > shown.length && (
            <div className="pl-4 text-faint">… {entries.length - shown.length} more</div>
          )}
          <div>{c}</div>
        </>
      )}
    </div>
  )
}

const JsonTree = ({ value }: { value: unknown }) => {
  const { style, data } = useCodeStyle()
  return (
    <div
      className="code card px-3 py-2 font-[family-name:var(--font-code)] text-[12.5px] leading-snug overflow-x-auto"
      style={style}
      data-testid="file-json"
      {...data}
    >
      <div className="-ml-4">
        <JsonNode v={value} depth={0} />
      </div>
    </div>
  )
}

const TextViewers = ({ kind, file, url }: { kind: ViewerKind; file: ViewFile; url: string }) => {
  const cap = capFor(kind)
  const read = useAsync(`${url}|${cap}`, () => readBytes(url, cap))
  const text = useMemo(
    () => (read.data && kind !== 'font' ? new TextDecoder('utf-8').decode(read.data.bytes) : ''),
    [read.data, kind],
  )
  if (read.error) return <Failed what="the file" reason={read.error} onRetry={read.reload} />
  if (!read.data) return <Skeleton className="h-64" />
  const cut = read.data.cut
  const shownSize = formatSize(cap)
  if (kind === 'font') return <FontSpecimen bytes={read.data.bytes} name={file.name} cut={cut} />
  if (kind === 'markdown')
    return (
      <Frame className="px-4 py-3">
        <MdLite text={text} className="text-[15px] leading-6" testId="file-markdown" />
        <CutNote cut={cut} what={`the first ${shownSize}`} />
      </Frame>
    )
  if (kind === 'json') {
    if (cut) return <Details file={file} why={`JSON over ${shownSize} is not opened here.`} />
    let value: unknown
    try {
      value = JSON.parse(text)
    } catch (e) {
      return (
        <>
          <p className="text-xs text-faint mb-1">Not valid JSON ({(e as Error).message}); shown as text.</p>
          <CodeBlock code={text} lang="json" />
        </>
      )
    }
    return <JsonTree value={value} />
  }
  if (kind === 'csv') {
    const table = parseDelimited(text, separatorFor(file.name, text), CSV_MAX_ROWS)
    const [head, ...rows] = table.rows
    return (
      <>
        <div className="card overflow-auto max-h-[70vh]" data-testid="file-csv">
          <table className="text-[13px] border-collapse min-w-full">
            {head && (
              <thead className="sticky top-0 bg-panel">
                <tr>
                  {head.map((h, i) => (
                    <th
                      key={i}
                      className="text-left font-semibold px-2.5 py-1.5 border-b border-line whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-line/60">
                  {Array.from({ length: table.columns }, (_, j) => (
                    <td key={j} className="px-2.5 py-1 align-top whitespace-pre-wrap max-w-[28ch]">
                      {r[j] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CutNote cut={table.more || cut} what={`the first ${table.rows.length} rows`} />
      </>
    )
  }
  const lines = text.split('\n')
  if (kind === 'code' && lines.length <= CODE_MAX_LINES)
    return (
      <>
        <CodeBlock
          code={text.replace(/\n$/, '')}
          lang={CODE_LANG[extOf(file.name)]}
          className="my-0"
          testId="file-code"
        />
        <CutNote cut={cut} what={`the first ${shownSize}`} />
      </>
    )
  const shown = lines.slice(0, CODE_MAX_LINES * 4).join('\n')
  return (
    <>
      <pre
        className={cx(
          'card px-3 py-2 text-[12.5px] leading-snug overflow-x-auto',
          kind === 'code'
            ? 'whitespace-pre font-[family-name:var(--font-code)]'
            : 'whitespace-pre-wrap break-words',
        )}
        data-testid="file-text"
      >
        {shown}
      </pre>
      <CutNote cut={cut || shown.length < text.length} what="the start" />
    </>
  )
}

/** A font's specimen: the bytes loaded as a FontFace (never a URL in a stylesheet). */
const FontSpecimen = ({ bytes, name, cut }: { bytes: ArrayBuffer; name: string; cut: boolean }) => {
  const family = useMemo(() => `files-font-${Math.abs(hash(name + bytes.byteLength))}`, [name, bytes])
  const [state, setState] = useState<'loading' | 'ok' | 'bad'>('loading')
  useEffect(() => {
    if (cut) return
    let stale = false
    const face = new FontFace(family, bytes)
    face
      .load()
      .then((f) => {
        document.fonts.add(f)
        if (!stale) setState('ok')
      })
      .catch(() => !stale && setState('bad'))
    return () => {
      stale = true
      document.fonts.delete(face)
    }
  }, [family, bytes, cut])
  if (cut || state === 'bad')
    return <Details file={{ id: '', name }} why="This font could not be read here." />
  return (
    <Frame className="px-4 py-4" data-testid="file-font">
      <div
        style={{ fontFamily: state === 'ok' ? `"${family}"` : undefined }}
        className="space-y-3 break-words"
      >
        <p className="text-[44px] leading-tight">Aa Bb Cc</p>
        <p className="text-2xl">The quick brown fox jumps over the lazy dog</p>
        <p className="text-base">ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789 &amp;!?</p>
        <p className="text-sm">Sphinx of black quartz, judge my vow.</p>
      </div>
    </Frame>
  )
}

const hash = (s: string) => {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return h
}

const ZipList = ({ url, size }: { url: string; size: number }) => {
  const z = useAsync(`${url}|zip`, () => listZip(rangeReader(url), size, 1000))
  if (z.error) return <Failed what="the archive's list" reason={z.error} onRetry={z.reload} />
  if (!z.data) return <Skeleton className="h-48" />
  return (
    <div data-testid="file-zip">
      <p className="text-sm text-muted mb-2 flex items-center gap-2">
        <FolderArchive size={16} aria-hidden /> {z.data.total} {z.data.total === 1 ? 'entry' : 'entries'}
        {z.data.entries.length < z.data.total && (
          <span className="text-faint">the first {z.data.entries.length} shown</span>
        )}
      </p>
      <ul className="group-list max-h-[65vh] overflow-y-auto">
        {z.data.entries.map((e, i) => (
          <li key={i} className="px-3.5 py-2 flex items-baseline justify-between gap-3">
            <span className={cx('min-w-0 break-all text-[14px]', e.dir && 'text-muted')}>{e.name}</span>
            {!e.dir && <span className="text-xs text-faint shrink-0 tabular">{formatSize(e.size)}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The viewer for one file. */
export const FileViewer = ({
  file,
  getUrl,
  onDownload,
}: {
  file: ViewFile
  getUrl: GetUrl
  /** absent on a view-only share: no Download offered anywhere */
  onDownload?: () => void
}) => {
  const kind = viewerFor(file.name, file.contentType)
  const url = useAsync(kind === 'none' ? null : `${file.id}|view`, () => getUrl('view'))
  const [broken, setBroken] = useState<string | null>(null)
  if (kind === 'none') return <Details file={file} onDownload={onDownload} />
  if (url.error) return <Failed what="the file" reason={url.error} onRetry={url.reload} />
  if (url.loading || url.data === undefined)
    return url.loading ? (
      <Skeleton className="h-64" />
    ) : (
      <Details file={file} onDownload={onDownload} why="This file is not ready to show." />
    )
  const src = url.data
  if (broken === src)
    return <Details file={file} onDownload={onDownload} why="This browser cannot show this file." />
  const fail = () => setBroken(src)
  switch (kind) {
    case 'image':
    case 'svg':
      return (
        <Frame className={cx('flex justify-center', kind === 'svg' && 'bg-raised')}>
          <img
            src={src}
            alt={file.name}
            className="max-h-[72vh] w-auto max-w-full object-contain"
            onError={fail}
            data-testid="file-image"
          />
        </Frame>
      )
    case 'video':
      return (
        <Frame className="bg-black">
          <video
            src={src}
            controls
            playsInline
            preload="metadata"
            className="w-full max-h-[72vh]"
            onError={fail}
            data-testid="file-video"
          />
        </Frame>
      )
    case 'audio':
      return (
        <Frame className="px-4 py-5">
          <p className="text-[15px] font-medium mb-3 break-all">{file.name}</p>
          <audio
            src={src}
            controls
            preload="metadata"
            className="w-full"
            onError={fail}
            data-testid="file-audio"
          />
        </Frame>
      )
    case 'pdf':
      return (
        <Suspense fallback={<Skeleton className="h-[60vh]" />}>
          <PdfViewer url={src} onFail={fail} />
        </Suspense>
      )
    case 'zip':
      return <ZipList url={src} size={file.size ?? 0} />
    default:
      return readsBytes(kind) ? (
        <TextViewers kind={kind} file={file} url={src} />
      ) : (
        <Details file={file} onDownload={onDownload} />
      )
  }
}
